import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Coins, Check, ChevronRight } from 'lucide-react';
import { useWallet } from '../contexts/WalletContext';
import { useAuth } from '../contexts/AuthContext';
import { usePlan } from '../contexts/PlanContext';
import { useIsDesktop } from '../hooks/useMediaQuery';
import SheetOrPage from '../components/chatHome/SheetOrPage';
import LedgerList from '../components/wallet/LedgerList';
import BalanceCard from '../components/wallet/BalanceCard';
import TopUpPacks from '../components/wallet/TopUpPacks';
import StarterPackCard from '../components/wallet/StarterPackCard';
import { getTopUpInfo, getWalletLedger, type TopUpPack, type LedgerEntry } from '../services/walletService';
import { trackCheckoutStarted, trackPurchase, trackStarterPackOfferShown, trackStarterPackOfferClicked } from '../services/analyticsService';

/**
 * Wallet — focused, low-clutter. One balance card (one currency, WP), the plan/subscription as the
 * primary action, top-ups demoted to a single coarse ladder (modal on desktop, page on mobile), and
 * only a 5-row history preview (full history lives on its own page).
 */
/** Seller-licence prices (USD/mo) for the GA4 purchase `value` — mirrors the plan cards + backend PLAN_PRICE_USD.
 *  The paid plans grant no credits (owner 2026-09-19); they activate selling. */
const PLAN_PRICE_USD: Record<'plus' | 'teams', number> = { plus: 30, teams: 100 };

const WalletPage: React.FC = () => {
  const { wp, cap, neverFunded, plan, renewsAt, loading, refresh: refreshWallet } = useWallet();
  const { user } = useAuth();
  // The next renewal/charge date comes from Stripe (the subscription anniversary), synced by the webhook —
  // NOT computed client-side. Null until the webhook has recorded the billing period.
  const nextRenewal = plan !== 'free' && renewsAt ? new Date(renewsAt) : null;
  const isDesktop = useIsDesktop();
  const [promoCode, setPromoCode] = useState('');
  const [promoBusy, setPromoBusy] = useState(false);
  const [promoMsg, setPromoMsg] = useState('');
  const [promoOk, setPromoOk] = useState(false);
  const [packs, setPacks] = useState<TopUpPack[]>([]);
  const [promoBonus, setPromoBonus] = useState(0);
  // The first-purchase starter pack (TRY-3): its definition always, offered only while the server says so.
  const [starterPack, setStarterPack] = useState<TopUpPack | null>(null);
  const [starterEligible, setStarterEligible] = useState(false);
  const offerStarter = starterEligible && starterPack ? starterPack : null;
  // Every pack a checkout on this page can name — for pricing analytics, including a starter bought moments ago.
  // TRY-4 funnel: the wallet showed the starter pack (once per eligibility), and a tap on it (the checkout event follows).
  const starterOffered = !!offerStarter;
  useEffect(() => { if (starterOffered) trackStarterPackOfferShown('wallet'); }, [starterOffered]);
  const buyStarter = (packId: string) => { trackStarterPackOfferClicked('wallet'); return topUp(packId); };
  const knownPacks = useMemo(() => (starterPack ? [starterPack, ...packs] : packs), [starterPack, packs]);
  const [ledger, setLedger] = useState<LedgerEntry[]>([]);
  const { planTier } = usePlan();
  const [buying, setBuying] = useState('');
  const [checkoutMsg, setCheckoutMsg] = useState('');
  const [planBusy, setPlanBusy] = useState(false);
  const [planMsg, setPlanMsg] = useState('');
  const [topUpOpen, setTopUpOpen] = useState(false);

  // Creator is the free plan's NAME, not an absence — a creator on it is not on a lesser tier, they are
  // simply not selling yet. 'Free' read as a limitation of the product rather than a description of the plan.
  const planLabel = planTier === 'PRO' ? 'Wisetree Plus' : planTier === 'ENTERPRISE' ? 'Wisetree Studio' : 'Creator';

  const upgrade = async (id: 'plus' | 'teams') => {
    setPlanBusy(true); setPlanMsg('');
    trackCheckoutStarted('subscription', id, PLAN_PRICE_USD[id]);
    try { const { startCheckout } = await import('../services/billingService'); const r = await startCheckout('subscription', id); if (!r.ok) setPlanMsg(r.message || ''); }
    catch { setPlanMsg('Could not start checkout.'); } finally { setPlanBusy(false); }
  };
  const manageSubscription = async () => {
    setPlanBusy(true); setPlanMsg('');
    try { const { openBillingPortal } = await import('../services/billingService'); const r = await openBillingPortal(); if (!r.ok) setPlanMsg(r.message || ''); }
    catch { setPlanMsg('Could not open billing.'); } finally { setPlanBusy(false); }
  };
  useEffect(() => {
    getTopUpInfo().then((r) => {
      setPacks(r.packs); setPromoBonus(r.promoBonus); setStarterPack(r.starterPack); setStarterEligible(r.starterPackEligible);
    }).catch(() => {});
  }, []);
  useEffect(() => { getWalletLedger({ limit: 5 }).then(setLedger).catch(() => {}); }, []);

  const topUp = async (packId: string) => {
    setBuying(packId); setCheckoutMsg('');
    trackCheckoutStarted('topup', packId, knownPacks.find((p) => p.id === packId)?.price_usd);
    try {
      const { startCheckout } = await import('../services/billingService');
      const r = await startCheckout('topup', packId);   // redirects to Stripe on success
      if (!r.ok) setCheckoutMsg(r.message || 'Checkout isn’t available yet.');
    } catch { setCheckoutMsg('Could not start checkout. Please try again.'); }
    finally { setBuying(''); }
  };

  // Completed a Stripe Checkout → Stripe redirects back to /wallet?upgraded=<plan> or ?toppedup=<pack>. Fire the
  // GA4 `purchase` event ONCE (then strip the param so a refresh can't double-count). Waits for packs so a top-up
  // carries its USD value. The webhook is what actually granted the entitlement — this is measurement only.
  const [purchaseTracked, setPurchaseTracked] = useState(false);
  useEffect(() => {
    if (purchaseTracked) return;
    const params = new URLSearchParams(window.location.search);
    const upgraded = params.get('upgraded');
    const toppedup = params.get('toppedup');
    const clear = () => window.history.replaceState({}, '', window.location.pathname);
    if (upgraded && (upgraded === 'plus' || upgraded === 'teams')) {
      trackPurchase('subscription', upgraded, PLAN_PRICE_USD[upgraded]);
      setPurchaseTracked(true); clear();
    } else if (toppedup) {
      const pack = knownPacks.find((p) => p.id === toppedup);
      if (pack) { trackPurchase('topup', toppedup, pack.price_usd, pack.credits); setPurchaseTracked(true); clear(); }
    }
  }, [knownPacks, purchaseTracked]);

  const redeemPromo = async () => {
    const code = promoCode.trim();
    if (!code) return;
    setPromoBusy(true); setPromoMsg('');
    try {
      const { redeemPromoCode } = await import('../services/walletService');
      const r = await redeemPromoCode(code);
      setPromoOk(true); setPromoMsg(`Code applied — +${(r.wp || 0).toLocaleString()} WP added to your wallet.`);
      setPromoCode('');
      await refreshWallet();
    } catch (e: any) {
      setPromoOk(false); setPromoMsg(e?.message || 'Could not redeem that code.');
    } finally { setPromoBusy(false); }
  };

  return (
    <div className="min-h-screen bg-white dark:bg-black text-secondary-900 dark:text-white">
      <div className="max-w-2xl mx-auto px-6 py-10">
        {/* NO "back to chats" LINK (owner 2026-08-24). The activity bar is on every signed-in page now, so a
            per-page back link is a second way to do the same thing — and it says "chats", which stopped being
            what the creator came from. Hierarchical backs stay (wallet -> history); a flat back does not. */}
        <BalanceCard wp={wp} cap={cap} plan={plan} nextRenewal={nextRenewal} loading={loading} neverFunded={neverFunded} />

        {/* Plan / subscription — the PRIMARY action */}
        <section className="mt-5 rounded-2xl border border-secondary-200 dark:border-secondary-700 p-5">
          <div className="flex items-center justify-between gap-3">
            <div>
              <p className="text-sm text-secondary-500">Your plan</p>
              <p className="text-lg font-semibold inline-flex items-center gap-2" data-testid="current-plan">
                {planLabel}
                {planTier !== 'FREE' && <span className="text-[11px] uppercase tracking-wide rounded-full bg-secondary-900 dark:bg-white text-white dark:text-secondary-900 px-2 py-0.5">Active</span>}
              </p>
            </div>
            {planTier !== 'FREE' && (
              <button onClick={manageSubscription} disabled={planBusy}
                className="rounded-xl border border-secondary-300 dark:border-secondary-700 px-3 py-2 text-sm font-medium hover:border-secondary-500 disabled:opacity-50">
                {planBusy ? 'Opening…' : 'Manage subscription'}
              </button>
            )}
          </div>

          {planTier === 'FREE' && (
            /*
             * TWO PLANS, SIDE BY SIDE, AND THE DIFFERENCE IN ONE SENTENCE (owner 2026-09-20).
             *
             * Creator sits on the LEFT because it is the plan the reader is already on — a comparison starts
             * from where you stand. The third tier (Wisetree Studio, $100) is withheld everywhere for now
             * (owner 2026-09-20): offering it to someone who has not sold anything asks them to judge hosted
             * capacity they cannot yet feel, and room is its only difference from Plus. It stays fully
             * configured — price, ceiling, Stripe id, invariant row — so returning it is one flag.
             *
             * Each card answers the same two questions in the same order — what you can do, and what it costs
             * — so the eye can compare rows rather than hunt prose.
             */
            <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
              {/* LEFT — where the reader already is. */}
              <div className="rounded-xl border border-secondary-300 dark:border-secondary-600 p-4 flex flex-col">
                <p className="font-semibold">
                  Creator <span className="text-sm font-normal text-secondary-500">$0</span>
                  <span className="ml-2 text-[11px] uppercase tracking-wide rounded-full border border-secondary-300 dark:border-secondary-600 px-2 py-0.5 text-secondary-500">Your plan</span>
                </p>
                <ul className="mt-2 space-y-1 text-xs text-secondary-500 flex-1">
                  <li>Write, cast and render episodes with the full crew</li>
                  <li>Publish to your own store address and give episodes away</li>
                  <li>Your first storyboard is on us; after that, credits pay for what you generate</li>
                  <li>Room for one complete series</li>
                </ul>
                <p className="mt-3 text-xs text-secondary-400">Everything except charging for an episode.</p>
              </div>

              {/* RIGHT — the one thing the other plan adds. */}
              <div className="rounded-xl border border-secondary-200 dark:border-secondary-700 p-4 flex flex-col">
                <p className="font-semibold">Wisetree Plus <span className="text-sm font-normal text-secondary-500">${PLAN_PRICE_USD.plus}/mo</span></p>
                <ul className="mt-2 space-y-1 text-xs text-secondary-500 flex-1">
                  <li>Everything in Creator</li>
                  <li><span className="text-secondary-700 dark:text-secondary-200">Put a price on your episodes</span> — you keep 90%</li>
                  <li>Give the opening episodes away, charge for the rest</li>
                  {/* SIZED IN SERIES, NOT BYTES (owner 2026-09-20). "200 MB of hosted media" read as a limit
                      to budget against and invited two fair questions — what happens when I need more, and
                      why am I paying for storage. A creator can picture twenty series; nobody can picture a
                      megabyte. The exact figure still appears in the usage bar above, which is where someone
                      asking "how much have I got left" actually looks. */}
                  <li>Room for about twenty full series</li>
                </ul>
                <p className="mt-3 text-xs text-secondary-400">A licence to sell. It adds no credits, and storage is included.</p>
                <button onClick={() => upgrade('plus')} disabled={planBusy} data-testid="upgrade-seller"
                  className="mt-3 px-3 py-2 rounded-xl bg-secondary-900 dark:bg-white text-white dark:text-secondary-900 text-sm font-medium hover:bg-black dark:hover:bg-secondary-100 disabled:opacity-50">
                  {planBusy ? 'Starting…' : 'Start selling'}
                </button>
              </div>
            </div>
          )}
          {planMsg && <p className="mt-3 text-sm text-secondary-500" data-testid="plan-msg">{planMsg}</p>}

          {/* Top-up — SECONDARY: one button; promo-code redemption on the right */}
          <div className="mt-5 pt-4 border-t border-secondary-100 dark:border-secondary-800">
            {/* CREDITS ARE NOT A PLAN, and this is the sentence that says so. The old line — "need more
                credits before your next refresh?" — described a monthly grant that no longer exists, so it
                told a creator to wait for something that was never coming. */}
            <p className="text-sm font-semibold text-secondary-900 dark:text-white">Credits</p>
            <p className="mt-1 text-xs text-secondary-500">
              Credits are what generating spends — writing, images, voices and video each cost some. They are
              not part of a plan: you buy them when you need them, on any plan, and they never expire.
            </p>
            {/* THE FIRST OPTION for an account that has never bought a pack (TRY-3): the starter, above the ladder. */}
            {offerStarter && (
              <div className="mt-4">
                <StarterPackCard pack={offerStarter} buying={buying} onBuy={buyStarter} />
              </div>
            )}
            <div className={`${offerStarter ? 'mt-3' : 'mt-2'} flex flex-wrap items-center justify-between gap-3`}>
              <button onClick={() => setTopUpOpen(true)} data-testid="open-topup"
                className="inline-flex items-center gap-2 rounded-xl border border-secondary-300 dark:border-secondary-700 px-3 py-2 text-sm font-medium hover:border-secondary-500">
                <Coins className="w-4 h-4 text-secondary-500" /> Top up credits
              </button>
              {/* Promo code */}
              <form onSubmit={(e) => { e.preventDefault(); redeemPromo(); }} className="flex items-center gap-2">
                <input value={promoCode} onChange={(e) => setPromoCode(e.target.value.toUpperCase())}
                  placeholder="Promo code" maxLength={24} aria-label="Promo code"
                  className="w-28 rounded-xl border border-secondary-300 dark:border-secondary-700 bg-white dark:bg-secondary-800 px-3 py-2 text-sm tracking-wide uppercase placeholder:normal-case placeholder:tracking-normal text-secondary-900 dark:text-white focus:outline-none focus:border-secondary-500" />
                <button type="submit" disabled={promoBusy || !promoCode.trim()}
                  className="rounded-xl bg-secondary-900 dark:bg-white px-3 py-2 text-sm font-medium text-white dark:text-secondary-900 hover:bg-black dark:hover:bg-secondary-100 disabled:opacity-40">
                  {promoBusy ? '…' : 'Redeem'}
                </button>
              </form>
            </div>
            {promoMsg && <p className={`mt-2 text-sm ${promoOk ? 'text-secondary-700 dark:text-secondary-200' : 'text-secondary-500 dark:text-secondary-400'}`} data-testid="promo-msg">{promoMsg}</p>}
          </div>
        </section>

        {checkoutMsg && <p className="mt-4 text-sm text-secondary-500" data-testid="checkout-msg">{checkoutMsg}</p>}

        {/* History — collapsed preview (last 5), full list on its own page */}
        <section className="mt-8">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold">Recent activity</h2>
            <Link to="/wallet/history" className="inline-flex items-center gap-1 text-sm text-secondary-500 hover:text-secondary-800 dark:hover:text-secondary-200">
              View all <ChevronRight className="w-4 h-4" />
            </Link>
          </div>
          <LedgerList entries={ledger} timezone={user?.timezone} emptyHint="No activity yet — grants, spends, and top-ups will show here." />
        </section>
      </div>

      {/* Top-up chooser — modal on desktop, full-screen page on mobile */}
      <SheetOrPage
        open={topUpOpen}
        asPage={!isDesktop}
        title="Top up credits"
        icon={<Coins className="h-4 w-4 text-secondary-500" />}
        onClose={() => setTopUpOpen(false)}
        maxWidth="max-w-lg"
      >
        {promoBonus > 0 && (
          <div className="mb-3 rounded-xl border border-secondary-300 dark:border-secondary-600 bg-secondary-100 dark:bg-secondary-800 px-3 py-2 text-xs font-medium inline-flex items-center gap-2">
            <Check className="w-3.5 h-3.5" /> +{Math.round(promoBonus * 100)}% bonus on every top-up
          </div>
        )}
        {offerStarter && (
          <div className="mb-4">
            <StarterPackCard pack={offerStarter} buying={buying} onBuy={buyStarter} />
          </div>
        )}
        <TopUpPacks packs={packs} promoBonus={promoBonus} buying={buying} onTopUp={topUp} />
        <p className="mt-3 text-xs text-secondary-400">Secure checkout via Stripe — credits land as soon as payment clears.</p>
      </SheetOrPage>
    </div>
  );
};

export default WalletPage;
